import { useMemo } from 'react';
import { channelLegendRichInfo } from '../../components/ChannelLabel';
import { chartAllColors } from '../../constants';
import { getPercentageInfo, mathFormat } from '../../utils';
import { isArray } from 'lodash-es';
import { dealwithLineSymbol } from './utils';
import { getChartBufferWidth } from '@op/utils/systemUtil';
import { isNilOrEmpty } from '@op/utils/value';

type EChartsOptions = echarts.EChartsOption;

export type ChartOptionsType = {
  userOptions: EChartsOptions;
  type?: 'line' | 'bar' | 'pie' | 'treemap';
  pieLabelAlignTo?: 'none' | 'labelLine' | 'edge';
  noMerge?: boolean;
  pieValueFormat?: (value: number) => any;
  excludeOptions?: Array<
    'tooltip' | 'grid' | 'legend' | 'color' | 'xAxis' | 'yAxis' | 'axisPointer'
  >; // 排除部分属性合并
  handleShow?: boolean; // 点击的时候选中态
  xAxisLabelInterval?: //  坐标轴刻度标签的显示间隔
  'auto' | number | ((index: number, value: string) => boolean);
  selectedMode?: boolean;
  pieTotal?: number;
  isMarketing?: boolean;
};

export default function useChartOptions({
  userOptions,
  type = 'line',
  pieLabelAlignTo = 'edge',
  excludeOptions = [],
  noMerge = false,
  handleShow = false,
  xAxisLabelInterval = 'auto',
  pieValueFormat = mathFormat,
  selectedMode = false,
  pieTotal,
  isMarketing = false,
}: ChartOptionsType) {
  // legend受到series的itemStyle影响较大
  const commonItemStyle = {
    borderColor: '#fff',
    borderWidth: 1,
  };

  const commonOptions: any = useMemo(() => {
    const options: any = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderWidth: 0,
        borderRadius: 6,
        confine: true, // 是否将弹窗限制再图表内
        // appendToBody: true, 将弹窗添加到body上
        // enterable: true, 是否允许鼠标进入弹窗
        hideDelay: 200,
        triggerOn: 'mousemove',
        padding: 0,
        axisPointer: {
          animation: false,
          type: type === 'line' ? 'line' : 'shadow',
        },
      },
      axisPointer: {
        link: { xAxisIndex: 'all' },
        label: {
          backgroundColor: '#fff',
          show: true,
          fontSize: 12,
          color: '#262832',
          fontWeight: 700,
          margin: 8,
          padding: [1, 7],
        },
        lineStyle: {
          type: 'solid',
          color: handleShow
            ? 'rgba(38, 40, 50, 0.55)'
            : 'rgba(38, 40, 50, 0.25)',
        },
        handle: {
          show: handleShow,
          opacity: 0,
        },
      },
      grid: {
        left: getChartBufferWidth(),
        right: 20,
        bottom: '30px',
        top: 6,
        containLabel: true,
      },
      legend: {
        type: 'scroll',
        icon: 'circle',
        selectedMode: true,
        orient: 'horizontal',
        itemHeight: 8,
        itemWidth: 8,
        bottom: 0,
        itemGap: 18,
        inactiveBorderWidth: 0,
        inactiveBorderColor: '#fff',
        pageIconColor: 'rgb(47, 69, 84)', // 翻页下一页的三角按钮颜色
        pageIconInactiveColor: 'rgb(170, 170, 170)', // 翻页（即翻页到头时）
        pageIconSize: 12, // 翻页按钮大小
        pageButtonPosition: 'end', // start
        textStyle: {
          color: '#888888',
          fontSize: 12,
          height: 14,
          lineHeight: 14,
          rich: {
            ...channelLegendRichInfo(16, 16, false, isMarketing),
            name: {
              width: 14,
              height: 14,
              lineHeight: 14,
              verticalAlign: 'middle',
              padding: [2, 0, 0, 0],
            },
          },
          verticalAlign: 'middle', // 文本垂直对齐方式
        },
        itemStyle: commonItemStyle,
        padding: [2, 0, 2, 0],
        selectorLabel: {
          verticalAlign: 'middle',
        },
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        axisLabel: {
          color: '#838697',
          fontSize: 12,
          align: 'center',
          interval: xAxisLabelInterval,
        },
        axisTick: {
          alignWithLabel: true,
        },
        axisLine: {
          lineStyle: {
            type: 'solid',
            color: 'rgba(0,0,0,0.25)',
            width: 1, // 坐标线的宽度
          },
          onZero: false,
        },
        axisPointer: {
          label: { show: !excludeOptions.includes('axisPointer') },
        },
      },
      yAxis: {
        type: 'value',
        alignTicks: true, // 轴线对齐
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: '#E5E5E5',
          },
        },
        axisLabel: {
          color: '#838697',
          fontSize: 12,
        },
      },
      color: chartAllColors,
    };
    if (type === 'pie') {
      // 这个影响到tooltip弹窗
      delete options.tooltip.trigger;
    }

    return options;
  }, [type]);

  // 这里得饼图配置会覆盖默认配置
  const commonPieOptions = {
    type: 'pie',
    radius: ['40%', '60%'],
    top: 10,
    bottom: 10,
    label: {
      color: '#262832',
      alignTo: pieLabelAlignTo,
      formatter: (params: any) => {
        // hooks参数传入total
        // series添加total | 占比
        // commonPieOptions提取为方法，在饼图页面调用
        const { value, percent, name } = params || {};
        let realPercent = `${percent.toFixed(2)}%`;
        if (!isNilOrEmpty(pieTotal)) {
          realPercent = getPercentageInfo(value, pieTotal || 0, 2);
        }
        return `{value|${name} }：{a|${pieValueFormat(
          value,
        )}(${realPercent})}\n{hr|}\n{a|}`;
      },
      edgeDistance: '20%',
      distanceToLabelLine: '0',
      rich: {
        a: {
          color: '#262832',
          lineHeight: 20, // 设置最后一行空数据高度，为了能让延长线与hr线对接起来
          align: 'center',
        },
        hr: {
          // 设置hr是为了让中间线能够自适应长度
          borderColor: 'auto', // hr的颜色为auto时候会主动显示颜色的
          width: '102%',
          borderWidth: 1,
          height: 0,
        },
        per: {
          // 用百分比数据来调整下数字位置，显的好看些。如果不设置，formatter最后一行的空数据就不需要
          padding: [4, 0],
        },
      },
    },
    labelLine: { width: 2 },
    emphasis: { focus: 'series' },
    selectedMode,
  };

  const commonLineOptions = {
    symbol: 'circle',
    showSymbol: true,
    symbolSize: 9,
    showAllSymbol: true, // 折线图显示所有节点
    emphasis: {
      // focus: 'series',
      itemStyle: {
        borderColor: '#fff',
        borderWidth: 1,
        borderType: 'solid',
        opacity: 1,
      },
    },
  };

  const commonBarOptions = {
    barMaxWidth: 80,
    // emphasis: {
    //   focus: 'series',
    // },
  };

  const optionsTypeMap = {
    pie: ['tooltip', 'grid', 'legend', 'color'],
    bar: [
      'yAxis',
      'tooltip',
      'grid',
      'legend',
      'color',
      'xAxis',
      'axisPointer',
    ],
    line: [
      'tooltip',
      'grid',
      'legend',
      'color',
      'xAxis',
      'yAxis',
      'axisPointer',
    ],
    treemap: ['tooltip', 'legend'],
  };

  const isObject = (obj: any) => typeof obj === 'object' && !Array.isArray(obj);
  const getCommonItemStyle = (item: any, itemStyle = {}) => {
    return {
      ...(item?.itemStyle || {}),
      ...commonItemStyle,
      ...itemStyle,
    };
  };

  const mergeSeriesOptions = ({
    resultOptions,
    commonOptions,
    specialItemStyle = {},
  }: {
    resultOptions: any;
    commonOptions: any;
    specialItemStyle?: any;
  }) => {
    // 统一参数
    let { series } = resultOptions;
    if (!Array.isArray(series)) {
      series = [series];
    }
    // 统一参数
    const getOptionsFunc =
      typeof commonOptions === 'function' ? commonOptions : () => commonOptions;

    return series.map((item: any) => {
      const noItemStyle = item?.type === 'bar';
      const { data, ...otherOptions } = getOptionsFunc(item);
      return {
        ...otherOptions,
        ...(item || {}),
        data: data || item?.data,
        itemStyle: noItemStyle
          ? { ...(item?.itemStyle || {}) }
          : getCommonItemStyle(item, specialItemStyle),
      };
    });
  };

  const dealwithPie = (resultOptions: any) => {
    // 对饼图进行圆角等操作
    if (type === 'pie') {
      resultOptions.series = mergeSeriesOptions({
        resultOptions,
        commonOptions: commonPieOptions,
        specialItemStyle: { borderRadius: 6 },
      });
    }
  };

  const dealwithBarLine = (resultOptions: any) => {
    // 对饼图进行圆角等操作
    if (type !== 'pie') {
      resultOptions.series = mergeSeriesOptions({
        resultOptions,
        commonOptions: (item: any) => {
          const isLineItem = item?.type === 'line';
          let data = item?.data || [];
          if (isLineItem) {
            data = dealwithLineSymbol(data);
          }
          return {
            ...(isLineItem ? commonLineOptions : commonBarOptions),
            ...item,
            data: data,
          };
        },
      });
    }
  };

  function mergeObjOptions(
    commonOption: { [key: string]: any },
    userOption: { [key: string]: any },
  ) {
    const option: any = {};
    for (const key in commonOption) {
      const common = commonOption[key];
      const user = userOption[key];
      if (isObject(common) && isObject(user)) {
        option[key] = Object.assign({}, common, userOption[key] || {});
      }
    }
    return {
      ...commonOption,
      ...userOption,
      ...(option || {}),
    };
  }

  const options = useMemo(() => {
    if (noMerge) return userOptions;
    const userOptionsInfo = userOptions || {};
    const optionsKey = optionsTypeMap?.[type]
      ?.concat(Object.keys(userOptionsInfo))
      ?.filter(key => !excludeOptions.includes(key));
    const resultOptions: { [key: string]: any } = { ...userOptionsInfo };
    // console.log(optionsKey);
    optionsKey?.forEach(key => {
      const userOption: any = userOptionsInfo[key]; // 用户配置内容
      const commonOption: any = commonOptions[key]; // 公共配置内容
      // 用户没有配置，公共有配置
      if (!userOption && commonOption) {
        resultOptions[key] = commonOption;
      }
      // 用户有配置，公共没有配置
      else if (userOption && !commonOption) {
        resultOptions[key] = userOption;
      }
      // 用户，公共都有配置, 最多两层的对象合并，没有必要深层次
      else {
        if (isObject(commonOption) && isObject(userOption)) {
          resultOptions[key] = mergeObjOptions(commonOption, userOption);
        } else if (isObject(commonOption) && isArray(userOption)) {
          resultOptions[key] = userOption.map(item =>
            mergeObjOptions(commonOption, item),
          );
        } else {
          resultOptions[key] = userOption;
        }
      }
    });

    dealwithPie(resultOptions);
    dealwithBarLine(resultOptions);

    // type === 'line' && console.log(resultOptions, 'resultOptions');
    return resultOptions;
  }, [userOptions]);

  return options;
}
